<template>
  <div>
    <div id="timeline-svg-wrapper">
      <VuePerfectScrollbar class="scroll-area" :settings="settings" @ps-scroll-x="handleScrollX">
        <svg id="timeline-svg" class="timeline"></svg>
      </VuePerfectScrollbar>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line
import { Bus } from '@/components/Bus'
// import { mapGetters } from 'vuex'
import Snap from 'snapsvg-cjs'
import SnapTimelineMarker from '@/components/SnapTimelineMarker'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'

const dimensions = {
  width: 1366,
  height: 600,
  edges: 150
}
const markerPadding = 70
const sinishPath = 'M-727.8,391.3c155.9-48.6,311.8-128.6,491.6-128.6c306.2,0,438.3,123.4,685.9,132.4c247.6,8.9,371.5-119.7,644.7-167c182.1-31.5,408-31.5,677.7,0c349.3,39.1,576.6,93.5,682,163.3'

export default {
  name: 'SnapTimeline',
  props: {},
  components: {
    VuePerfectScrollbar
  },
  data: function () {
    return {
      chartData: this.$store.getters.getChartData,
      svg: null,
      group: null,
      timelinePath: null,
      selectableVerticies: [],
      markers: [],
      selectedData: null,
      redraw: false,
      settings: {
        suppressScrollY: true,
        maxScrollbarLength: 150
      }
    }
  },
  computed: {
    ageData: function () {
      return this.$store.getters.getFilteredAgeData.slice(0)
    }
  },
  mounted: function () {
    // Create the new Snap SVG
    this.createSvg()
    // this.addDebugBounds()
    this.createPath()
    this.calculateStartAndEndOfUsablePath()
    this.applyMarkers()

    Bus.$on('filterChanged', () => {
      this.applyMarkers()
    })

    Bus.$on('redrawMarkers', () => {
      this.applyMarkers()
    })
  },
  methods: {
    handleScrollX: function () {
      document.documentElement.classList.add('hide-swipe')
    },
    createSvg: function () {
      this.svg = Snap('#timeline-svg')
      this.svg.attr({
        version: '1.2',
        viewBox: `0,0,${dimensions.width},${dimensions.height}`,
        width: dimensions.width,
        height: dimensions.height
      })
      // 'shape-rendering': 'crispEdges'
      this.group = this.svg.group()
      // this.addDebugBounds()
    },
    createPath: function () {
      this.timelinePath = this.group.path(sinishPath)
      this.timelinePath.attr({
        class: 'timeline__path'
      })
    },
    applyMarkers: function () {
      const bounds = this.getDataBounds(this.ageData.sort((a, b) => {
        return a.age - b.age
      }, {
        age: 0
      }))

      const zerodData = this.zeroData(this.ageData, bounds.min.age)
      const yearGap = this.breakPathIntoYears(this.selectableVerticies, bounds.min.age, bounds.max.age)

      const firstXVerticies = this.selectableVerticies[0].idx
      let lastXVerticies = 0

      zerodData.forEach((elm, idx) => {
        let cx = 0
        let cy = 0
        let verticies = lastXVerticies
        let event = elm.event.charAt(0).toUpperCase() + elm.event.slice(1).replace(/-/g, ' ')

        if (elm.age !== 0) {
          verticies = Math.floor(firstXVerticies + (elm.zeroedAge * yearGap))
          cx = this.timelinePath.getPointAtLength(verticies).x
          cy = this.timelinePath.getPointAtLength(verticies).y

          while (this.timelinePath.getPointAtLength(verticies).x < lastXVerticies.x + markerPadding) {
            cx = this.timelinePath.getPointAtLength(verticies).x
            cy = this.timelinePath.getPointAtLength(verticies).y
            verticies += 1
          }

          lastXVerticies = this.timelinePath.getPointAtLength(verticies)
        }

        const markerOptions = {
          targetVerticies: {
            x: cx,
            y: cy,
            idx: verticies
          },
          age: elm.age,
          event: event,
          feelings: this.chartData.charts.feelings[elm.event],
          attitude: this.chartData.charts.attitude[elm.event],
          chartInfo: this.chartData.charts.info[elm.event],
          cost: this.chartData.charts.cost[elm.event] || '',
          timeline: this.timelinePath,
          initDelay: 1600 - (200 * idx)
        }

        if (this.redraw) {
          this.markers[idx].update(markerOptions)
        } else {
          const marker = new SnapTimelineMarker(Object.assign({}, {id: idx}, markerOptions))
          this.group.add(marker.svg)
          this.markers[idx] = marker
        }
      })
      this.redraw = true
    },
    addDebugBounds: function () {
      this.svg.rect(0, 0, dimensions.width, dimensions.height).attr({
        'stroke-width': '1',
        'stroke': '#ff0000',
        'fill': 'none'
      })
    },
    breakPathIntoYears: function (arr, min, max) {
      return arr.length / (max - min)
    },
    calculateStartAndEndOfUsablePath: function () {
      const totalLength = Math.floor(this.timelinePath.getTotalLength())
      for (let i = 0; i < totalLength; i++) {
        const pal = this.timelinePath.getPointAtLength(i)
        if (pal.x >= dimensions.edges && pal.x <= dimensions.width - dimensions.edges) {
          this.selectableVerticies.push({
            idx: i,
            vert: pal
          })
        }
      }
    },
    zeroData: function (data, min) {
      return data.map(elm => {
        return Object.assign({}, elm, {zeroedAge: elm.age - min})
      })
    },
    getDataBounds: function (data) {
      return {
        min: data.reduce((cur, prev) => {
          return cur.age < prev.age && cur.age !== 0 ? cur : prev
        }, { age: Number.POSITIVE_INFINITY }),
        max: data.reduce((cur, prev) => {
          return cur.age > prev.age && cur.age !== 0 ? cur : prev
        }, { age: Number.NEGATIVE_INFINITY })
      }
    }
  }
}
</script>

<style lang="scss">

@import '../styles/core/vars.scss';
@import '../styles/core/mixins.scss';

svg {
  overflow: visible;
}

#timeline-svg-wrapper{
  position: relative;

  justify-content: center;
  align-items: center;

  @include respond-to('large') {
    display: flex;
  }
}

.svg .ps {
  @include respond-to('large') {
    overflow: visible !important
  }

  .ps__scrollbar-y-rail {
    display: block;

    @include respond-to('large') {
      display: none !important;
    }
  }

  &:hover>.ps__scrollbar-x-rail:hover {
    background-color: rgba(227,16,93,0.2);
  }
}

.timeline{
  overflow: visible;
  width: 1280px;

  &__path{
    stroke: #fff;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 5;
    fill: none;
  }

  &__marker{
    fill: $c-blue;
    stroke: $c-white;
    stroke-width: 3px;
  }
}

.marker{
  $self: &;
  cursor: pointer;

  &--planning-my-funeral {
    fill: $c-white;

    .marker__age__background {
      fill: $c-white;
    }

    .marker__age__text {
      fill: $c-blue;
    }
  }

  &__age {

    > {
      pointer-events: none;
    }
    &__background {
      fill: #335b81;
      stroke: $c-white;
      stroke-width: 3px;
    }
    &__text {
      fill: $c-white;
      font-size: 20px;
      text-anchor: middle;
      font-weight: 700;
    }
  }
  &__event-text {
    fill: $c-white;
    font-family: $f-body;
    text-anchor: middle;
    pointer-events: none;
    transition: opacity 0.3s ease-in-out;

    &--odd{
      dominant-baseline: text-after-edge;
      tspan{
        dominant-baseline: text-before-edge;
      }
    }

    &--even{
      dominant-baseline: text-before-edge;
      tspan{
        dominant-baseline: text-before-edge;
      }
    }

    .settled & {
      opacity: 1;
    }
  }
  &__line-to-event{
    stroke: $c-white;
    stroke-width: 1px;
    fill: $c-white;
    // opacity: 0;
    transition: all 0.3s ease-in-out;

    .settled & {
      opacity: 1;
    }
  }
  &__hover{
    opacity: 0;
    transform: scale(0);
    transition: all 0.3s ease-in-out;

    circle{
      stroke: none;
      fill: $c-white;
      fill-opacity: 0.5;
    }
  }

  &:hover{
    #{$self}__hover{
      opacity: 1;
      transform: scale(1);
    }
  }

}

</style>



// WEBPACK FOOTER //
// src/components/SnapTimeline.vue